import {
  APIViewpointAttributes,
  DiffMode,
  JSONEdge,
  JSONEdgeLegend,
  JSONGraph,
  JSONLegend,
  JSONNode,
  JSONNodeLegend,
} from '@ardoq/api-types';
import { Action, dispatchAction } from '@ardoq/rxbeach';
import { getComponentTypesForLegend } from '@ardoq/view-legend';

import {
  LucidChartDocumentPickerStatus,
  LucidDocumentSelectPayload,
  RequiredAuth,
} from './types';
import {
  LUCID_CHART_URL,
  DOCUMENT_PICKER_URL,
  LUCIDCHART_INVALID_SESSION,
  MAX_AUTH_ATTEMPTS,
  EMBED_VIEWER_URL,
} from './const';
import { exportToLucidchart as exportToLucidchartAction } from './actions';
import { currentDate, format } from '@ardoq/date-time';
import { from, tap } from 'rxjs';
import { api, ApiResponse } from '@ardoq/api';
import { ArdoqError, isTruthy, isArdoqError } from '@ardoq/common-helpers';
import { startOAuthFlow } from 'oauth/actions';
import { showToast, ToastType } from '@ardoq/status-ui';
import { ParentChildGraphEdge } from '@ardoq/graph';
import { I18nLocale, t } from '@ardoq/i18n';
import { uniqBy } from 'lodash';
import { getActiveDiffMode } from '../../scope/activeDiffMode$';
import { isInScopeDiffMode } from '../../scope/scopeDiff';
import {
  addComponentColors,
  addReferenceColors,
  isParentChildEdgeId,
  markCollapsedGroupsAsLeafNodes,
} from '../../graphExport/enrichJSONGraph';
import { GraphExportModelData } from '../../graphExport/types';
import { rgbaToHex } from 'tabview/blockDiagram/view/yFilesExtensions/shared/styleUtils';

export const isSuccessDocumentSelection = (
  event: any
): event is LucidDocumentSelectPayload =>
  event?.status === LucidChartDocumentPickerStatus.SUCCESS;

export const createDocumentPickerUrl = ({
  token,
  titleSuggestion,
}: {
  token: string;
  titleSuggestion: string;
}) => {
  const params = new URLSearchParams({
    token,
    allowDocumentCreation: 'true',
    autoSelectCreatedDocument: 'true',
    newDocumentTitleSuggestion: titleSuggestion,
  });

  return `${DOCUMENT_PICKER_URL}?${params.toString()}`;
};

export const exportToLucidchart = (isLegendActive: boolean) => {
  dispatchAction(exportToLucidchartAction({ attemptCount: 0, isLegendActive }));
};

export const createEmbedViewerUrl = ({
  documentId,
  clientId,
}: {
  documentId: string;
  clientId: string;
}) => {
  const params = new URLSearchParams({
    clientId,
    document: documentId,
    mode: 'viewer',
  });
  return `${EMBED_VIEWER_URL}?${params.toString()}`;
};

const measureTextWidth = (text: string) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    return 0;
  }
  context.font = '12px Arial';
  return context.measureText(text).width;
};

const getCompLegendTypes = (nodeList: JSONNode[]): JSONNodeLegend[] => {
  return getComponentTypesForLegend(
    nodeList.map(node => node.componentId).filter(isTruthy)
  ).map((legendType): JSONNodeLegend => ({
    name: legendType.label,
    nameWidth: measureTextWidth(legendType.label),
    color: rgbaToHex(legendType.color),
    representationData: legendType.representationData.isImage
      ? {
          isImage: true,
          value: legendType.representationData.value ?? undefined,
        }
      : {
          isImage: false,
          shapeName: legendType.representationData.shapeName ?? undefined,
          icon: legendType.representationData.icon?.id ?? undefined,
        },
  }));
};

const getRefLegendTypes = (
  edgeList: JSONEdge[],
  referenceModelTypesById: GraphExportModelData['referenceModelTypesById']
): JSONEdgeLegend[] => {
  const normalRefTypes = uniqBy(
    edgeList
      .map(edge => edge.referenceId)
      .filter(isTruthy)
      .map(referenceId => referenceModelTypesById[referenceId])
      .filter(isTruthy),
    'name'
  );

  const hasParentChildEdges = edgeList.some(edge =>
    isParentChildEdgeId(edge.referenceId)
  );

  return normalRefTypes
    .concat(hasParentChildEdges ? [ParentChildGraphEdge.getType()] : [])
    .map((referenceType): JSONEdgeLegend => ({
      color: rgbaToHex(referenceType.color),
      line: referenceType.line,
      lineBeginning: referenceType.lineBeginning,
      lineEnding: referenceType.lineEnding,
      name: referenceType.name,
      nameWidth: measureTextWidth(referenceType.name),
    }));
};

const getLegend = (
  nodeList: JSONNode[],
  edgeList: JSONEdge[],
  modelData: GraphExportModelData
): JSONLegend => {
  const activeDiffMode = isInScopeDiffMode() ? getActiveDiffMode() : null;

  if (activeDiffMode === DiffMode.DIFF) {
    return {
      diffMode: activeDiffMode,
      nodeTypes: [],
      edgeTypes: [],
    };
  }

  return {
    diffMode: activeDiffMode,
    nodeTypes: getCompLegendTypes(nodeList),
    edgeTypes: getRefLegendTypes(edgeList, modelData.referenceModelTypesById),
  };
};

export const getJSONGraph = (
  jsonData: JSONGraph,
  isLegendActive: boolean,
  modelData: GraphExportModelData
): JSONGraph => {
  const updatedNodeList = addComponentColors(jsonData.nodeList, modelData);
  const finalNodeList = markCollapsedGroupsAsLeafNodes(updatedNodeList);
  const updatedEdgeList = addReferenceColors(jsonData.edgeList, modelData);
  const legend = isLegendActive
    ? getLegend(jsonData.nodeList, jsonData.edgeList, modelData)
    : undefined;

  const jsonGraph: JSONGraph = {
    ...jsonData,
    nodeList: finalNodeList,
    edgeList: updatedEdgeList,
    legend,
  };

  return jsonGraph;
};

export const getExportLucidDocumentTitle = (
  traversals: APIViewpointAttributes[],
  i18nLocale: I18nLocale
): string => {
  const currentDateTime = currentDate();
  const timestamp = format(currentDateTime, 'yyyy-MM-dd HH:mm');

  const title =
    traversals.length > 0
      ? traversals[0].name
      : t(i18nLocale, 'lucidchart:export.defaultDocumentTitle', 'Ardoq Export');

  return `${title} - ${timestamp}`;
};

type HandleAuthErrorArgs = {
  operation: string;
  failedMessage: string;
  attemptCount: number;
  error: ArdoqError;
  onCompleted: VoidFunction;
};

const handleLucidchartAuthError = ({
  operation,
  failedMessage,
  attemptCount,
  error,
  onCompleted,
}: HandleAuthErrorArgs) => {
  if (
    api.isForbidden(error) &&
    error.userMessage === LUCIDCHART_INVALID_SESSION &&
    attemptCount < MAX_AUTH_ATTEMPTS
  ) {
    return dispatchAction(
      startOAuthFlow({
        provider: 'lucidchart',
        onCompleted,
        operation,
      })
    );
  }

  showToast(failedMessage, ToastType.INFO);
};

export const authRequiredLucidchartAction = <
  Response,
  LucidActionResult extends ApiResponse<Response>,
  Payload extends RequiredAuth,
>({
  lucidchartAction,
  onCompleteAction,
  payload,
  operation,
  failedMessage,
}: {
  lucidchartAction: () => LucidActionResult;
  onCompleteAction: (payload: Payload) => Action<unknown>;
  payload: Payload;
  operation: string;
  failedMessage: string;
}) => {
  return from(lucidchartAction()).pipe(
    tap(response => {
      if (isArdoqError(response)) {
        const attemptCount = payload.attemptCount ?? 0;
        handleLucidchartAuthError({
          operation,
          failedMessage,
          attemptCount,
          error: response,
          onCompleted: () =>
            dispatchAction(
              onCompleteAction({
                ...payload,
                attemptCount: attemptCount + 1,
              })
            ),
        });
      }
    })
  );
};

export const getLucidEditUrl = (documentId: string) =>
  `${LUCID_CHART_URL}/${documentId}/edit`;

const LUCIDCHART_DOCUMENT_URL = new RegExp(
  `${LUCID_CHART_URL.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/([^/\\s,]+)`,
  'g'
);

export const toLucidchartDocumentIds = (text: string): string[] => [
  ...new Set(
    [...text.matchAll(LUCIDCHART_DOCUMENT_URL)].map(
      ([, documentId]) => documentId
    )
  ),
];
